/**
 * 全局配置 Model
 * 用于存储和管理全局配置信息，如支持的语言列表等
 *
 * 状态管理说明：
 * - languages: 支持的语言代码列表，如 ['en', 'zh', 'ko', 'ja']
 * - languageNames: 语言代码到中文名称的映射，如 { en: '英语', zh: '中文' }
 * - loading: 加载状态
 *
 * 使用方式：
 * const { languages, languageNames, fetchConfig } = useModel('config');
 */
import { useCallback, useEffect, useState } from 'react';
import { ComicService } from '@/facades/contentCatalog';
import { LANGUAGE_LABELS, SUPPORTED_APP_LANGUAGES } from '@/utils/languages';

// 默认语言名称映射（备用）
const DEFAULT_LANGUAGE_NAMES: Record<string, string> = {
  ...LANGUAGE_LABELS,
};

export default function useConfig() {
  // 支持的语言列表
  const [languages, setLanguages] = useState<string[]>(SUPPORTED_APP_LANGUAGES);
  // 语言名称映射
  const [languageNames, setLanguageNames] = useState<Record<string, string>>(
    DEFAULT_LANGUAGE_NAMES,
  );
  // 加载状态
  const [loading, setLoading] = useState<boolean>(false);
  const [environment, setEnvironment] = useState<string>('local');

  // 获取配置信息
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const config = await ComicService.getAdminConfig();
      if (config && typeof config === 'object') {
        const configObj = config as Record<string, unknown>;
        // 更新语言列表
        if (Array.isArray(configObj.supported_languages)) {
          setLanguages(configObj.supported_languages as string[]);
        }
        // 更新语言名称映射
        if (
          configObj.language_names &&
          typeof configObj.language_names === 'object'
        ) {
          setLanguageNames({
            ...DEFAULT_LANGUAGE_NAMES,
            ...(configObj.language_names as Record<string, string>),
          });
        }
        if (typeof configObj.environment === 'string') {
          setEnvironment(configObj.environment);
        }
      }
    } catch (error) {
      console.error('获取配置失败', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  return {
    languages,
    languageNames,
    loading,
    environment,
    fetchConfig,
  };
}
